import { useQuery } from '@tanstack/react-query';
import { Badge, Card } from '@safetyhub/ui';
import {
  formatAnnexLabel,
  formatDateISO,
  type AdminUpcomingAdminRuleAmendment,
  type AdminUpcomingAdminRuleArticlesChange,
  type AdminUpcomingAdminRuleUnlinkedReason,
  type AdminUpcomingAmendment,
  type AdminUpcomingAmendmentAnnexChangeKind,
  type AdminUpcomingAmendmentAnnexImpact,
  type AdminUpcomingAmendmentArticleImpact,
} from '@safetyhub/shared';
import { extractApiError } from '../../lib/api';
import { getUpcomingAmendments, UPCOMING_AMENDMENTS_QUERY_KEY } from '../../lib/legalUpcomingAmendments';

const IMPACT_LABEL: Record<
  AdminUpcomingAmendmentArticleImpact,
  { label: string; tone: 'rose' | 'slate' | 'amber' }
> = {
  CHANGED: { label: '인용 조 변경', tone: 'rose' },
  UNCHANGED: { label: '인용 조 변경 없음', tone: 'slate' },
  UNKNOWN: { label: '원문 확인 필요', tone: 'amber' },
};

/** 의무 단위 별표 영향(S3) — 조건 줄의 근거 별표 키가 이 개정본의 별표를 가리킬 때만 뜬다 */
const ANNEX_IMPACT_LABEL: Record<
  AdminUpcomingAmendmentAnnexImpact,
  { label: string; tone: 'rose' | 'slate' | 'amber' }
> = {
  CHANGED: { label: '근거 별표 변경', tone: 'rose' },
  UNCHANGED: { label: '근거 별표 변경 없음', tone: 'slate' },
  UNKNOWN: { label: '근거 별표 대조 불가', tone: 'amber' },
};

const ANNEX_CHANGE_LABEL: Record<AdminUpcomingAmendmentAnnexChangeKind, string> = {
  CHANGED: '변경',
  ADDED: '신설',
  REMOVED: '삭제',
};

/**
 * 「바뀐 별표」 한 줄 — 서버 `annexChangesOf`의 결과를 그린다(설계 2026-11-01 §4-② · 처방 ④). `annexBaseline`이 `CURRENT`일 때만
 * 「변경 없음」을 말한다 — 현행본 별표가 수집되지 않았거나(백필 전) 현행본이 없으면 「대조 불가」이지 「변경 없음」이 아니다.
 */
function annexSummary(
  a: Pick<AdminUpcomingAmendment, 'annexBaseline' | 'changedAnnexes' | 'futureAnnexCount'>,
): { text: string; tone: 'rose' | 'slate' | 'amber' } | null {
  if (a.annexBaseline === 'NO_CURRENT_VERSION') {
    return { text: '별표 대조 불가 — 현행 시행본이 없습니다', tone: 'amber' };
  }
  if (a.annexBaseline === 'NOT_COLLECTED') {
    return {
      text: `별표 대조 불가 — 현행 시행본의 별표가 수집되지 않았습니다(백필 전) · 개정본 별표 ${a.futureAnnexCount}건`,
      tone: 'amber',
    };
  }
  if (a.changedAnnexes.length > 0) {
    return {
      text: `바뀐 별표 ${a.changedAnnexes.length}건: ${a.changedAnnexes
        .map((c) => {
          const label = formatAnnexLabel('별표', c.annexNumber, c.annexSubNumber);
          return `${c.title ? `${label}(${c.title})` : label} ${ANNEX_CHANGE_LABEL[c.change]}`;
        })
        .join(', ')}`,
      tone: 'rose',
    };
  }
  if (a.futureAnnexCount > 0) {
    return { text: `별표 변경 없음 (${a.futureAnnexCount}건 대조)`, tone: 'slate' };
  }
  return null;
}

/**
 * 시행일까지 남은 날 — **로컬 달력 날짜의 차**다(옆에 그리는 「시행 YYYY-MM-DD」와 같은 축).
 *
 * 종전 `ceil((시행 시각 − 지금) / 1일)`은 시행일이 UTC 자정으로 저장된 행에서 KST 00:00~09:00에
 * 하루를 더 셌다(리뷰 1103). 저장 형태가 UTC 자정(prod 컨테이너의 `new Date(y, m, d)`)이든
 * KST 자정이든 **로컬 날짜**는 같은 날이므로 그 날짜끼리 뺀다(`Date.UTC`로 DST 없이 센다).
 */
export function daysUntil(iso: string, now: Date): number {
  const enf = new Date(iso);
  const enfDay = Date.UTC(enf.getFullYear(), enf.getMonth(), enf.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((enfDay - today) / 86_400_000);
}

/**
 * **시행 예정 개정** — 2단계 L25(US-0724 「플랫폼 운영사로서, 시행 예정 개정을 미리 보고 준비한다」 · 기대 「공포에서
 * 시행까지 실측 6개월이라 그 창을 쓸 수 있다」). 수신 범위는 **운영사만**(사용자 결정 2026-10-28) — 이 패널이 유일한
 * 표면이고 업체·기관에는 알리지 않는다.
 *
 * 무엇을 싣는지(시행일이 미래인 법령 행 · 법제처 법령ID로 이어진 활성 의무 · 조 단위 영향)는 서버
 * `LegalObligationReviewQueueService.listUpcomingAmendments`가 정한다. 이 패널은 그것을 그린다 — 「인용 조 변경」인
 * 의무가 준비할 일이고, 「원문 확인 필요」는 조 표기를 기계가 대조하지 못한 것이다(변경 없음으로 단정하지 않는다).
 *
 * ⚠️ 이 패널은 카탈로그를 **고치지 않는다** — 고치는 길은 아래 목록의 「수정」(카탈로그 서비스의 유일한 변경 경로)이다.
 */
export default function UpcomingAmendmentsPanel() {
  const { data, isLoading, error } = useQuery({
    queryKey: UPCOMING_AMENDMENTS_QUERY_KEY,
    queryFn: getUpcomingAmendments,
  });

  return (
    <section aria-labelledby="upcoming-amendments-heading" className="mb-6">
      <h2 id="upcoming-amendments-heading" className="mb-2 text-lg font-bold text-gray-900">
        시행 예정 개정
      </h2>
      {isLoading ? (
        <div className="py-4 text-sm text-gray-500">불러오는 중...</div>
      ) : error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
          시행 예정 개정을 불러오지 못했습니다. {extractApiError(error).message}
        </div>
      ) : !data || data.amendments.length === 0 ? (
        <Card padding="none" className="px-4 py-4 text-sm text-gray-500">
          시행일이 아직 오지 않은 근거 법령 개정이 없습니다.
        </Card>
      ) : (
        <ul className="space-y-3" aria-label="시행 예정 개정 목록">
          {data.amendments.map((a) => (
            <AmendmentCard key={a.id} amendment={a} now={new Date(data.at)} />
          ))}
        </ul>
      )}
      {/* `?? []` — 배포 창에서 이 번들이 옛 서버(그 칸이 없는 응답)를 만날 수 있다 */}
      {data && <AdminRuleAmendments rules={data.adminRuleAmendments ?? []} now={new Date(data.at)} />}
    </section>
  );
}

/** 빈 근거 의무의 이유(서버가 정한다) — 「근거 의무 0개」로 읽히지 않게 이유를 그대로 적는다 */
const ADMIN_RULE_UNLINKED_LABEL: Record<AdminUpcomingAdminRuleUnlinkedReason, string> = {
  NO_ADMIN_RULE_SOURCE_KEY: '고시를 가리키는 근거 키가 아직 없어 근거 의무를 잇지 못했습니다',
};

const ADMIN_RULE_ARTICLES_LABEL: Record<AdminUpcomingAdminRuleArticlesChange, string> = {
  CHANGED: '본문 변경',
  UNCHANGED: '본문 변경 없음',
  NO_CURRENT_VERSION: '현행 고시 없음 — 본문 대조 불가',
};

/**
 * 시행 예정 **고시**(②-f2) — 법령과 별 표라 따로 그린다. ⚠️ 근거 의무는 지금 늘 비어 있고 서버가 그 이유
 * (`obligationsUnlinkedReason` — 고시를 가리키는 근거 키가 아직 없다)를 함께 준다 — 화면은 그 이유를 적는다. 빈 목록이면 「없다」와 함께 «수집 범위 단서»를 적는다 — 수집이 현행 목록만
 * 받아 시행 전 고시가 들어오는지 확인되지 않았으므로 0건을 「예정 개정 없음」으로 단정하지 않는다.
 */
function AdminRuleAmendments({ rules, now }: { rules: AdminUpcomingAdminRuleAmendment[]; now: Date }) {
  return (
    <div className="mt-4">
      <h3 className="mb-2 text-sm font-bold text-gray-900">시행 예정 고시 개정</h3>
      {rules.length === 0 ? (
        <Card padding="none" className="px-4 py-3 text-sm text-gray-500" data-testid="admin-rule-empty">
          시행일이 아직 오지 않은 고시 개정이 없습니다. 고시는 현행 목록으로 수집되어, 시행 전 고시가 이 목록에 들어오는지는 아직
          확인되지 않았습니다.
        </Card>
      ) : (
        <ul className="space-y-3" aria-label="시행 예정 고시 개정 목록">
          {rules.map((r) => {
            const annex = annexSummary({
              annexBaseline: r.annexBaseline,
              changedAnnexes: r.changedAnnexes,
              futureAnnexCount: r.futureAnnexCount,
            });
            return (
              <li key={r.id}>
                <Card padding="none" className="overflow-hidden">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-ink-200 px-4 py-3">
                    <p className="text-sm font-bold text-ink-900">{r.name}</p>
                    {r.amendmentType && <span className="text-xs text-ink-500">{r.amendmentType}</span>}
                    <p className="text-xs text-ink-600">
                      {r.issueDate ? `발령 ${formatDateISO(new Date(r.issueDate))} → ` : ''}
                      {`시행 ${formatDateISO(new Date(r.enforcementDate))}`}
                      {` (${daysUntil(r.enforcementDate, now)}일 남음)`}
                    </p>
                    <p className="ml-auto text-xs text-ink-500">
                      {`${ADMIN_RULE_ARTICLES_LABEL[r.articlesChange]} · 조문 ${r.articleCount}`}
                    </p>
                  </div>
                  {annex && (
                    <p
                      data-testid="annex-summary"
                      className={`px-4 pt-2 text-xs ${
                        annex.tone === 'rose' ? 'text-rose-700' : annex.tone === 'amber' ? 'text-amber-700' : 'text-ink-500'
                      }`}
                    >
                      {annex.text}
                    </p>
                  )}
                  <p className="px-4 py-3 text-xs text-ink-500" data-testid="admin-rule-obligations">
                    {r.obligationsUnlinkedReason
                      ? `${ADMIN_RULE_UNLINKED_LABEL[r.obligationsUnlinkedReason]} — 원문을 읽고 해당 조건을 확인하세요.`
                      : r.obligations.length === 0
                        ? '이 고시를 근거로 쓰는 활성 의무가 없습니다.'
                        : `이 고시를 근거로 쓰는 의무 ${r.obligations.length}개: ${r.obligations.map((o) => o.title).join(', ')}`}
                  </p>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AmendmentCard({ amendment: a, now }: { amendment: AdminUpcomingAmendment; now: Date }) {
  const changedCount = a.obligations.filter((o) => o.articleImpact === 'CHANGED').length;
  const annexChangedCount = a.obligations.filter((o) => o.annexImpact === 'CHANGED').length;
  const annex = annexSummary(a);
  return (
    <li>
      <Card padding="none" className="overflow-hidden">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-ink-200 px-4 py-3">
          <p className="text-sm font-bold text-ink-900">{a.nameKr}</p>
          {a.amendmentType && <span className="text-xs text-ink-500">{a.amendmentType}</span>}
          <p className="text-xs text-ink-600">
            {a.promulgationDate ? `공포 ${formatDateISO(new Date(a.promulgationDate))} → ` : ''}
            {`시행 ${formatDateISO(new Date(a.enforcementDate))}`}
            {` (${daysUntil(a.enforcementDate, now)}일 남음)`}
          </p>
          <p className="ml-auto text-xs text-ink-500">
            {`변경 조문 ${a.changedArticles.length} / 시행 예정 조문 ${a.futureArticleCount}`}
          </p>
        </div>
        {a.changedArticles.length > 0 && (
          <p className="px-4 pt-2 text-xs text-ink-600">
            {`변경 조문: ${a.changedArticles
              .map((c) => (c.title ? `${c.articleNoDisplay}(${c.title})` : c.articleNoDisplay))
              .join(', ')}`}
          </p>
        )}
        {annex && (
          <p
            data-testid="annex-summary"
            className={`px-4 pt-2 text-xs ${
              annex.tone === 'rose' ? 'text-rose-700' : annex.tone === 'amber' ? 'text-amber-700' : 'text-ink-500'
            }`}
          >
            {annex.text}
          </p>
        )}
        <div className="px-4 py-3">
          <p className="mb-1 text-xs font-semibold text-ink-700">
            {a.lawId === null
              ? '이 개정본에 법령ID가 없어 근거 의무를 잇지 못했습니다 — 법규 관리에서 그 행을 확인하세요.'
              : a.obligations.length === 0
              ? '이 법령을 근거로 쓰는 활성 의무가 없습니다.'
              : `이 법령을 근거로 쓰는 의무 ${a.obligations.length}개 · 인용 조 변경 ${changedCount}개` +
                (annexChangedCount > 0 ? ` · 근거 별표 변경 ${annexChangedCount}개` : '')}
          </p>
          {a.obligations.length > 0 && (
            <ul aria-label={`${a.nameKr} 근거 의무`} className="divide-y divide-ink-100">
              {a.obligations.map((o) => {
                // 조 영향이 null = 조건의 근거 별표 키로만 이어진 의무(근거 조는 다른 법령이다 — shared docblock)
                const impact = o.articleImpact ? IMPACT_LABEL[o.articleImpact] : null;
                const annexImpact = o.annexImpact ? ANNEX_IMPACT_LABEL[o.annexImpact] : null;
                return (
                  <li key={o.key} className="flex items-center gap-3 py-1.5 text-sm">
                    <span className="min-w-0 flex-1 truncate text-ink-900">{o.title}</span>
                    <span className="flex-none text-xs text-ink-500">
                      {impact
                        ? (o.statuteArticleRef ?? '조 표기 없음')
                        : `근거 ${o.sourceAnnexes.map((a) => formatAnnexLabel('별표', a.annexNumber, a.annexSubNumber)).join(', ')}`}
                    </span>
                    {impact && (
                      <Badge tone={impact.tone} size="sm">
                        {impact.label}
                      </Badge>
                    )}
                    {annexImpact && (
                      <Badge tone={annexImpact.tone} size="sm">
                        {annexImpact.label}
                      </Badge>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Card>
    </li>
  );
}
